<template>
    <div class="org-wrapper" dir="ltr">
        <div class="org-zoombar">
            <button type="button" @click="zoom(-0.1)" :disabled="scale <= minScale" aria-label="Zoom out">
                <app-icon name="minus"/>
            </button>
            <button type="button" @click="resetZoom" aria-label="Reset zoom">
                <app-icon name="maximize"/>
            </button>
            <button type="button" @click="zoom(0.1)" :disabled="scale >= maxScale" aria-label="Zoom in">
                <app-icon name="plus"/>
            </button>
        </div>

        <div class="org-viewport custom-scrollbar">
            <div class="org-zoom" :style="{ transform: `scale(${scale})` }">
                <organization-chart :datasource="chartData" :pan="false" @node-click="clickedNote">
                    <template #default="{ nodeData }">
                        <div class="org-node">
                            <div class="org-node-head">
                                <div class="org-node-dept">{{ nodeData.name }}</div>
                                <span class="org-node-count" :title="`${nodeData.count} employees (incl. sub-departments)`">
                                    <app-icon name="users"/> {{ nodeData.count }}
                                </span>
                            </div>
                            <div class="org-node-mgr">
                                <span class="org-node-av" :class="{ 'is-empty': !nodeData.title }">
                                    {{ nodeData.title ? initials(nodeData.title) : '—' }}
                                </span>
                                <span class="org-node-mgr-name">{{ nodeData.title || 'No manager' }}</span>
                            </div>
                        </div>
                    </template>
                </organization-chart>
            </div>
        </div>
    </div>
</template>

<script>
import OrganizationChart from 'vue3-organization-chart'
import 'vue3-organization-chart/dist/orgchart.css'

export default {
    name: "AppOrganizationChart",
    components: {
        OrganizationChart
    },
    props: {
        chartData: {
            type: Object,
            require: true
        },
        height: {
            type: Number,
            default: 530
        },
        druggable: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            scale: 1,
            minScale: 0.4,
            maxScale: 1.6,
        }
    },
    mounted() {
        // Default to a smaller scale on narrow screens so the whole structure fits.
        const w = window.innerWidth;
        this.scale = w < 576 ? 0.55 : (w < 992 ? 0.75 : 1);
    },
    methods: {
        clickedNote(value) {
            this.$emit('selected-item', value);
        },
        zoom(step) {
            const next = Math.round((this.scale + step) * 100) / 100;
            this.scale = Math.min(this.maxScale, Math.max(this.minScale, next));
        },
        resetZoom() {
            this.scale = 1;
        },
        initials(name) {
            if (!name) return '—';
            return name.trim().split(/\s+/).slice(0, 2).map(p => p.charAt(0).toUpperCase()).join('');
        }
    }
}
</script>

<style scoped>
.org-wrapper {
    position: relative;
}

.org-zoombar {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 5;
    display: flex;
    gap: 6px;
    background: var(--default-card-bg, #fff);
    border: 1px solid var(--default-border-color);
    border-radius: 10px;
    padding: 5px;
    box-shadow: 0 4px 12px rgba(8, 32, 92, 0.08);
}

.org-zoombar button {
    width: 30px;
    height: 30px;
    border: none;
    background: var(--base-color);
    border-radius: 7px;
    color: #2b3245;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}

.org-zoombar button:hover:not(:disabled) {
    background: #e6efff;
    color: #2563eb;
}

.org-zoombar button:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.org-zoombar :deep(svg) {
    width: 16px;
    height: 16px;
}

.org-viewport {
    height: 68vh;
    min-height: 380px;
    overflow: auto;
    background: #f6f8fc;
    border-radius: 14px;
}

.org-zoom {
    transform-origin: top left;
    transition: transform 0.15s ease;
    width: max-content;
    min-width: 100%;
    padding: 24px;
}

/* ---- override the library's default (red dashed grid) look ---- */
.org-zoom :deep(.orgchart-container) {
    border: none;
    height: auto !important;
    width: auto !important;
    overflow: visible;
    background: none;
}

.org-zoom :deep(.orgchart) {
    background-image: none;
    border: none;
    padding: 0;
    min-width: 0;
}

.org-zoom :deep(.orgchart > table:first-child) {
    margin: 0 auto;
}

/* connector lines: soft brand grey-blue instead of red */
.org-zoom :deep(.orgchart .lines .topLine) {
    border-top-color: #c3cee0;
}

.org-zoom :deep(.orgchart .lines .leftLine) {
    border-left-color: #c3cee0;
}

.org-zoom :deep(.orgchart .lines .rightLine) {
    border-right-color: #c3cee0;
}

.org-zoom :deep(.orgchart .lines .downLine) {
    background-color: #c3cee0;
}

/* node container: drop fixed width / dashed border / hover highlight */
.org-zoom :deep(.orgchart .node) {
    width: auto;
    padding: 8px;
    border: none;
}

.org-zoom :deep(.orgchart .node:hover),
.org-zoom :deep(.orgchart .node.focused) {
    background-color: transparent;
}

/* ---- modern node card (slot content) ---- */
.org-node {
    width: 200px;
    background: #ffffff;
    border: 1px solid var(--default-border-color);
    border-top: 3px solid #2563eb;
    border-radius: 12px;
    padding: 13px 16px;
    box-shadow: 0 6px 16px rgba(8, 32, 92, 0.07);
    text-align: left;
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.org-node:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 22px rgba(8, 32, 92, 0.14);
    border-top-color: #08205c;
}

.org-node-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
}

.org-node-dept {
    font-size: 14px;
    font-weight: 600;
    color: #08205c;
    line-height: 1.3;
    white-space: normal;
}

.org-node-count {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 11px;
    font-weight: 600;
    color: #1d4ed8;
    background: #e6efff;
    padding: 2px 8px;
    border-radius: 20px;
    white-space: nowrap;
}

.org-node-count :deep(svg) {
    width: 12px;
    height: 12px;
}

.org-node-mgr {
    display: flex;
    align-items: center;
    gap: 9px;
    margin-top: 10px;
}

.org-node-av {
    width: 30px;
    height: 30px;
    flex: none;
    border-radius: 50%;
    background: #2563eb;
    color: #ffffff;
    font-weight: 600;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.org-node-av.is-empty {
    background: #aeb6c6;
}

.org-node-mgr-name {
    font-size: 12.5px;
    color: #5b6273;
    white-space: normal;
    line-height: 1.25;
}
</style>
